<template>
  <AppModalEdit
    v-model="isShowing"
    :loading="loading"
    @clickExec="save"
    title="メニュー追加申請"
  >
    <v-container v-if="item != null" grid-list-md>
      <v-layout>
        <v-flex xs2>メニュー使用可能範囲</v-flex>
        <v-flex xs10>
          <SelectScope v-if="selectScopeMode" />
          <div v-else>{{ scopeString }}</div>
        </v-flex>
      </v-layout>
      <v-layout pt-2>
        <v-flex xs2>適用広告主</v-flex>
        <v-flex xs10>
          <SearchAndSelect
            :loading="searching"
            :searched-list="advertiserList"
            :selected-item-name="selectedAdvertiserName"
            @search="searchAdvertiser"
            @change="selectAdvertiser"
            button-text="広告主を検索"
          />
        </v-flex>
      </v-layout>
      <AdvancedSetting v-show="selectedAdvertiser" button-text="高度な設定">
        <v-layout>
          <v-flex xs2>適用案件</v-flex>
          <v-flex xs10>
            <SearchAndSelect
              :loading="searching"
              :searched-list="insertionOrderList"
              :selected-item-name="selectedInsertionOrderName"
              @search="searchInsertionOrder"
              @change="selectInsertionOrder"
              button-text="案件を検索"
            />
          </v-flex>
        </v-layout>
      </AdvancedSetting>
      <v-layout pt-4>
        <v-flex xs2>メニュー種類</v-flex>
        <v-flex xs10>
          <v-select
            v-model="item.menuType"
            :items="menuTypeList"
            disabled
            style="max-width:400px;"
          />
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs2>メニュー名</v-flex>
        <v-flex xs10>
          <v-text-field v-model="item.name" style="width:80%" />
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs2>メニュー説明</v-flex>
        <v-flex xs10>
          <v-textarea v-model="item.description" style="width:80%" />
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs2>課金形態</v-flex>
        <v-flex xs10>
          <v-select
            v-model="item.unitType"
            :items="unitTypeList"
            style="max-width:400px;"
          />
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs2>通貨</v-flex>
        <v-flex xs10>{{ displayCurrency }}</v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs2>配信単価</v-flex>
        <v-flex xs10>
          <v-text-field
            v-model="item.unitPrice"
            @keydown.arrow-down.prevent
            @keydown.arrow-up.prevent
            @wheel.prevent
            :disabled="item.unitType == 'cost_plus_margin'"
            prefix="￥"
            type="number"
            style="max-width:200px;"
          />
        </v-flex>
      </v-layout>
      <DateRangePicker
        :default-use-date-range="item.useDateRange"
        :start="item.startDate"
        :end="item.endDate"
        @change="changeDateRange"
        label="使用可能期間"
        uselabel="期間を指定する"
        not-uselabel="期間を指定しない"
      />
    </v-container>
  </AppModalEdit>
</template>

<script>
import { actions, snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import { CreateForm } from '@/models/Menu'
import SearchAndSelect from '@/components/Shared/CrudUI/SearchAndSelect'
import DateRangePicker from '@/components/Shared/CrudUI/DateRangePicker'
import AdvancedSetting from '@/components/Shared/CrudUI/AdvancedSetting'

export default {
  components: { SearchAndSelect, DateRangePicker, AdvancedSetting },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    advertiserList: {
      type: Array,
      default: () => [],
    },
    insertionOrderList: {
      type: Array,
      default: () => [],
    },
    searching: {
      type: Boolean,
      default: false,
    },
    systemOwner: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectScopeMode: false,
      item: undefined,
      menuTypeList: [{ value: 'geo', text: 'ジオターゲティング' }],
      unitTypeList: [
        { value: 'cpc', text: 'CPC（1クリック単価）' },
        { value: 'cpm', text: 'CPM（1000回表示あたり単価）' },
        {
          value: 'cost_plus_margin',
          text: 'オークションCPM（配信状況によって表示単価が変動）',
        },
      ],
      // 案件検索用
      loadingInsertionOrder: false,
      searchedInsertionOrderList: [],
      loading: false,
    }
  },
  computed: {
    scopeString() {
      if (!this.selectedAdvertiser) return ''
      return this.selectedInsertionOrder
        ? '案件 ' + this.selectedInsertionOrder.name
        : '広告主 ' + this.selectedAdvertiser.name + ' 全案件'
    },
    isShowing: {
      get() {
        return this.value
      },
      set(isDisplay) {
        return this.$emit('input', isDisplay)
      },
    },
    selectedAdvertiser() {
      return this.item.advertiser
    },
    selectedInsertionOrder() {
      return this.item.insertionOrder
    },
    selectedAdvertiserName() {
      return this.selectedAdvertiser ? this.selectedAdvertiser.name : ''
    },
    selectedInsertionOrderName() {
      return this.selectedInsertionOrder ? this.selectedInsertionOrder.name : ''
    },
    displayCurrency() {
      if (this.item.currency === 'JPY') {
        return '日本円（JPY）'
      }
      return this.item.currency
    },
  },
  watch: {
    value(val) {
      if (val) {
        this.init()
        return
      }
      this.item = undefined
    },
  },
  methods: {
    init() {
      const systemOwner = this.systemOwner
      const currency = 'JPY' // 当面、通貨は日本円固定
      this.item = new CreateForm({ systemOwner, currency })
      // 2019/06時点ではgeoのみなので初期設定してしまう
      this.item.menuType = 'geo'
    },
    changeSelectScopeMode() {
      this.selectScopeMode = true
    },
    changeDateRange(obj) {
      const start = obj.useDateRange ? obj.startDate : ''
      const end = obj.useDateRange ? obj.endDate : ''
      this.item.startDate = start
      this.item.endDate = end
    },
    async save() {
      const result = await this.$confirm(undefined, {
        body: '追加申請しますがよろしいですか？',
      })
      if (!result) return
      try {
        await this.item.execCreate()
        await this.$store.dispatch(actions.SHOW_SNACKBAR, {
          type: snackbarType.SUCCESS,
          message: '申請に成功しました',
        })
        this.$emit('save')
      } catch (e) {
        this.loading = false
        throw e
      }
      this.loading = false
    },
    // 広告主検索用
    searchAdvertiser(keyword) {
      this.$emit('searchAdvertiser', keyword)
    },
    selectAdvertiser(adv) {
      this.item.advertiser = adv
    },
    // 案件検索用
    searchInsertionOrder(keyword) {
      this.$emit('searchInsertionOrder', {
        keyword: keyword,
        id: this.selectedAdvertiser.id,
      })
    },
    selectInsertionOrder(io) {
      this.item.insertion_order = io
    },
  },
}
</script>

<style></style>
